import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../lib/api'

export interface SearchResult {
  id: string
  title: string
  crew: string
  teams: string[]
  phase: string
  stage: string
  path: string
  snippet: string
  score: number
}

export interface SearchFilters {
  crew?: string
  team?: string
  phase?: string
}

export function useSearch() {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const search = useCallback(async (q: string, filters: SearchFilters = {}) => {
    if (!q.trim()) {
      setResults([])
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await api.search({ q, ...filters })
      setResults(data.results || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed')
      setResults([])
    } finally {
      setLoading(false)
    }
  }, [])

  const debouncedSearch = useCallback(
    (q: string, filters: SearchFilters = {}) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
      debounceRef.current = setTimeout(() => {
        search(q, filters)
      }, 200)
    },
    [search]
  )

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  return {
    query,
    setQuery,
    results,
    loading,
    error,
    search,
    debouncedSearch,
  }
}
